/**
 * Short-URL QR contract (spec §1.3): join /j/<6> · quest /q/<6> ·
 * contact /c/<8> · order /o/<8>. QR content is always an absolute short URL
 * so native cameras deep-link into the PWA.
 */
export function shortUrl(kind: "j" | "q" | "c" | "o", code: string, origin?: string): string {
  const base = (origin ?? (typeof window !== "undefined" ? window.location.origin : "")).replace(/\/$/, "");
  return `${base}/${kind}/${code.toUpperCase()}`;
}

export const contactUrl = (code: string, origin?: string) => shortUrl("c", code, origin);
export const orderUrl = (code: string, origin?: string) => shortUrl("o", code, origin);
export const joinUrl = (code: string, origin?: string) => shortUrl("j", code, origin);
export const questUrl = (code: string, origin?: string) => shortUrl("q", code, origin);

/**
 * Extract the join code from a scanned /j/<code> short URL (client-side
 * mirror of @ec2/contracts parseQrContent — the contracts package pulls
 * server-only deps into the bundle, so the web app keeps this tiny parser).
 */
export function parseJoinCode(raw: string): string | null {
  try {
    const match = /^\/j\/([0-9A-HJKMNP-TV-Za-hjkmnp-tv-z]{6})\/?$/.exec(new URL(raw).pathname);
    return match ? match[1]!.toUpperCase() : null;
  } catch {
    return null;
  }
}

/**
 * XSS guard for participant-stored LinkedIn URLs: only http(s) URLs on a
 * linkedin.com hostname may ever become an <a href> (a stored `javascript:`
 * URL must never execute in a contact's browser).
 */
export function safeLinkedinHref(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  try {
    const parsed = new URL(url);
    const httpish = parsed.protocol === "https:" || parsed.protocol === "http:";
    if (httpish && /(^|\.)linkedin\.com$/i.test(parsed.hostname)) return parsed.href;
  } catch {
    /* not a URL */
  }
  return undefined;
}

/** Defense-in-depth for EXTERNAL_QR redirects: only http(s) may be opened. */
export function safeExternalHref(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  try {
    const parsed = new URL(url);
    if (parsed.protocol === "https:" || parsed.protocol === "http:") return parsed.href;
  } catch {
    /* not a URL */
  }
  return undefined;
}
